// «Verificar con Google» — tarjeta de Configuración de la Mesa de Servicio.
// Para aprobar la IA, el backend exige que el correo @codingcloud.online o
// @dumaxst.com de la persona lo haya probado Google al menos una vez (ver
// services/googleProof.ts). Aquí se ve el estado y se hace el enlace.
// La primera verificación retira la contraseña anterior (la pudo poner otra
// persona): aquí se pide la nueva y, al terminar, hay que volver a entrar.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { auth } from "../../../firebaseConfig";
import { logoutUser } from "../../../pages/login/functions";
import {
  fetchGoogleProofStatus,
  GoogleProofError,
  MIN_NEW_PASSWORD_LENGTH,
  verifyWithGoogle,
  type GoogleProofStatus,
} from "../services/googleProof";

interface Props {
  isDarkMode: boolean;
  primaryColor: string;
}

export function GoogleProofCard({ isDarkMode, primaryColor }: Props) {
  const { t } = useTranslation("serviceDesk");
  const [status, setStatus] = useState<GoogleProofStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [relogin, setRelogin] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchGoogleProofStatus()
      .then((s) => alive && setStatus(s))
      .catch(() => alive && setStatus(null));
    return () => {
      alive = false;
    };
  }, []);

  const onVerify = async () => {
    setError(null);
    if (newPassword.length < MIN_NEW_PASSWORD_LENGTH) {
      setError(t("settings.googleProof.errors.badPassword", { min: MIN_NEW_PASSWORD_LENGTH }));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t("settings.googleProof.errors.passwordMismatch"));
      return;
    }
    setBusy(true);
    try {
      const r = await verifyWithGoogle(auth, status?.email, newPassword);
      setStatus({ email: r.email, proven: true });
      setNewPassword("");
      setConfirmPassword("");
      if (r.reloginRequired) setRelogin(true);
    } catch (e) {
      const key = e instanceof GoogleProofError ? e.key : "failed";
      setError(t(`settings.googleProof.errors.${key}`, { email: status?.email || "", min: MIN_NEW_PASSWORD_LENGTH }));
    } finally {
      setBusy(false);
    }
  };

  const card = isDarkMode ? "bg-dark-surface border-dark-border" : "bg-white border-gray-200";
  const text = isDarkMode ? "text-white" : "text-gray-900";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const input = isDarkMode
    ? "w-full rounded-lg border border-dark-border bg-transparent px-3 py-2 text-sm text-white"
    : "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900";

  return (
    <div className={`rounded-xl border p-5 ${card}`} data-testid="google-proof-card">
      <h2 className={`mb-1 text-sm font-semibold ${text}`}>{t("settings.googleProof.title")}</h2>
      <p className={`mb-3 text-xs ${muted}`}>{t("settings.googleProof.description")}</p>
      {relogin ? (
        <div className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800 dark:bg-amber-500/10" data-testid="google-proof-relogin">
          <p className="mb-2">{t("settings.googleProof.reloginRequired")}</p>
          <button
            type="button"
            onClick={() => void logoutUser()}
            className="rounded-lg px-3 py-1.5 text-xs font-semibold text-white"
            style={{ backgroundColor: primaryColor }}
          >
            {t("settings.googleProof.relogin")}
          </button>
        </div>
      ) : status?.proven ? (
        <p className="rounded-lg bg-emerald-50 p-3 text-xs text-emerald-700 dark:bg-emerald-500/10" data-testid="google-proof-ok">
          {t("settings.googleProof.proven", { email: status.email || "" })}
        </p>
      ) : (
        <div className="space-y-2">
          <p className={`text-xs ${muted}`}>{t("settings.googleProof.newPasswordHelp", { min: MIN_NEW_PASSWORD_LENGTH })}</p>
          <input
            type="password"
            autoComplete="new-password"
            className={input}
            placeholder={t("settings.googleProof.newPassword")}
            aria-label={t("settings.googleProof.newPassword")}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            data-testid="google-proof-new-password"
          />
          <input
            type="password"
            autoComplete="new-password"
            className={input}
            placeholder={t("settings.googleProof.confirmPassword")}
            aria-label={t("settings.googleProof.confirmPassword")}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            data-testid="google-proof-confirm-password"
          />
          <button
            type="button"
            onClick={onVerify}
            disabled={busy}
            className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            style={{ backgroundColor: primaryColor }}
          >
            {busy ? t("settings.googleProof.verifying") : t("settings.googleProof.verify")}
          </button>
        </div>
      )}
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </div>
  );
}
